import { JSX, createEffect, createMemo, createSignal, onMount } from "solid-js";
import { FaqWidgetContext } from "./context";
import { useTranslation } from "~/primitives/translator";
import { createPdfGenerator } from "~/primitives/generator";
import { config } from "~/config";
import { i18nCache } from "~/stores/i18n";
import { useWidgetApp } from "~/provider";
import { cacheHandler } from "~/stores/cache";
import { FaqArticle, FaqCategory } from "../types";
import { fetchFaq } from "../repositories/catalog";
import { getFaqParam, isFaqLink, setFaqArticleParam } from "../widgets/faq/helpers";
import { useBookmark } from "~/primitives/bookmark";
import { useDeeplink } from "~/providers/DeeplinkProvider/context";

interface FaqWidgetProviderProps {
	id: string;
	children: JSX.Element;
}

export function FaqWidgetProvider(props: FaqWidgetProviderProps) {

	const { locale } = useWidgetApp();
	const { routeToDeeplinkOrUrl } = useDeeplink();

	// =================================================================

	const [selected, setSelected] = createSignal<FaqArticle>();
	const [categories, setCategories] = createSignal<FaqCategory[]>([]);
	const [search, setSearch] = createSignal<string>("");
	const [faqName, setFaqName] = createSignal<string>("");
	const [faqDownloadButton, setFaqDownloadButton] = createSignal<HTMLButtonElement>();
	const [openSheet, setOpenSheet] = createSignal(false);
	const [tab, setTab] = createSignal<"toc" | "bookmarks">("toc");

	// =================================================================

	const bookmark = useBookmark(props.id);
	const [isDownloading, download] = createPdfGenerator(
		() => "faq",
		() => useTranslation(selected()!.question)
	);

	const fetchFaqArticleTranslationFields = async (article: FaqArticle) => {

		const [question, mainAnswer, cateogry] = await Promise.all([
			useTranslation(article.question),
			useTranslation(article.mainAnswer),
			useTranslation(article.category.title)
		]);

		return [question, mainAnswer, cateogry];
	};

	onMount(async () => {

		const faqParam = getFaqParam();
		const faqCatalog = await cacheHandler(props.id, fetchFaq);

		if (!faqCatalog) {
			throw new Error(`FAQ Catalog with id ${props.id} not found`);
		}

		// fetch all translations for the faq catalog
		setFaqName(faqCatalog.title);
		setCategories(faqCatalog.categories);

		// fetch all translations for the faq catalog. 
		// This is due to the search functionality, we have to have the translations upfront.
		let article: FaqArticle | undefined;
		const articles: FaqArticle[] = [];
		const cats: string[] = [];

		for (const category of faqCatalog.categories) {
			cats.push(category.title);
			for (const item of category.articles) {
				articles.push(item);
				if (item.id === faqParam) {
					article = item;
				}
			}
		}

		if (article) {
			setSelected(article);
		} else {
			setSelected(articles[0]);
		}

		await Promise.all([
			useTranslation(faqCatalog.title),
			articles.map(fetchFaqArticleTranslationFields),
			cats.map(useTranslation)
		] as const);
	});

	createEffect(() => {
		const article = selected();

		if (article?.id) {
			setFaqArticleParam(article.id);
		}
	});

	const downloadFaq = async () => {

		const downloadButtonElement = faqDownloadButton();

		if (!downloadButtonElement) {
			throw new Error("Faq Download button not found");
		}

		const article = selected();
		const [question, mainAnswer, category] = await fetchFaqArticleTranslationFields(article!);

		download({ question, mainAnswer, category }, downloadButtonElement);
	};

	const textToSpeech = async () => {

		const article = selected();

		if (!article) {
			console.warn("No article selected");
			return "";
		}

		const [category, question, mainAnswer] = await Promise.all([
			useTranslation(article.category.title),
			useTranslation(article.question),
			useTranslation(article.mainAnswer)
		]);

		return [category, question, mainAnswer].join(". ");
	};

	const handleLinkClick = (event: MouseEvent) => {
		const href = (event.target as HTMLAnchorElement).href;

		if (!isFaqLink(href, props.id)) {
			routeToDeeplinkOrUrl(event);

			return;
		}

		event.preventDefault();

		// const articleId = href.replace(new RegExp(`https://${config.deepLinkEndpoint}/articles/${props.id}/`), '');
		const articleId = href.replace(new RegExp(`https://${config.deepLinkEndpoint}/faqs/${props.id}/item/`), '');
		const articles = categories().flatMap(category => category.articles);
		const article = articles.find(article => article.id === articleId);

		if (article) {
			setSelected(article);
		}
	};

	const searchedArticles = createMemo(() => {
		const searchValue = search().toLocaleLowerCase();
		const _categories = categories();

		if (!searchValue) {
			return _categories;
		}

		const result: FaqCategory[] = [];

		for (const category of _categories) {

			const articles = category.articles.filter(article => {
				const answer = i18nCache.cache.get(`${locale}:${article.mainAnswer}`) || "";
				const question = i18nCache.cache.get(`${locale}:${article.question}`) || "";

				return answer.toLowerCase().includes(searchValue) || question.toLowerCase().includes(searchValue);
			});

			if (articles.length) {
				result.push({ ...category, articles });
			}
		}

		return result;
	});

	const hasBookmarked = () => {
		const article = selected();

		if (!article) {
			return false;
		}

		return bookmark.hasBookmarked(article.id);
	}

	const toggleBookmark = () => {
		const article = selected();
		article && bookmark.bookmark(article);
	};

	return (
		<FaqWidgetContext.Provider value={{
			bookmark,
			categories,
			downloadFaq,
			isDownloading,
			search,
			selected,
			setSearch,
			setSelected,
			faqName,
			handleLinkClick,
			textToSpeech,
			searchedArticles,
			faqDownloadButton,
			setFaqDownloadButton,
			hasBookmarked,
			toggleBookmark,
			openSheet,
			setOpenSheet,
			tab,
			setTab
		}}>
			{props.children}
		</FaqWidgetContext.Provider>
	);
}